import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  Account,
  Assumptions,
  EventItem,
  LargePurchase,
  Scenario,
  ScenarioMeta,
  Snapshot,
} from '../types';
import {
  ensureSeeded,
  getActiveProfile,
  setActiveProfile as dbSetActiveProfile,
  listProfiles,
  hasAnyProfile,
  createProfile as dbCreateProfile,
  deleteProfile as dbDeleteProfile,
  duplicateProfile as dbDuplicateProfile,
  resetProfileToDemoSeed,
  completeOnboarding as dbCompleteOnboarding,
  pollActiveProfile,
  getAssumptions,
  getMeta,
  listAccounts,
  listAllAccounts,
  listPurchases,
  listScenarios,
  listSnapshots,
  putAccount,
  putAssumptions,
  putMeta,
  putPurchase,
  putScenario,
  putSnapshot,
  deleteAccount as dbDeleteAccount,
  deletePurchase as dbDeletePurchase,
  deleteScenario as dbDeleteScenario,
  deleteSnapshot as dbDeleteSnapshot,
  exportAll,
  importAll,
  wipe,
  DEFAULT_PROFILE,
  DEMO_PROFILE,
  type ProfileMeta,
} from '../db';
import type { OnboardingInput } from '../db/onboarding';
import { project, latestBalances, type ProjectionResult } from '../engine/projection';
import { computeDashboard, type DashboardStats } from '../engine/dashboard';

interface StoreCtx {
  ready: boolean;

  /** True on a fresh browser (no profiles exist yet) — the shell renders
   *  the welcome / onboarding page instead of the dashboard. Also true
   *  transiently when the user re-opens onboarding from an existing
   *  session (e.g. from the demo banner). */
  needsOnboarding: boolean;
  /** Materialize the default `main` profile from the welcome form and
   *  drop the visitor into the dashboard. */
  completeOnboarding: (input: OnboardingInput) => Promise<void>;
  /** Load the fictional sample dataset as the `demo` profile and activate
   *  it. Safe to call multiple times — creates the profile on first call,
   *  just switches to it thereafter. */
  startDemo: () => Promise<void>;
  /** Re-open the welcome / onboarding overlay from anywhere in the app —
   *  used by the demo banner's "Start your own plan" link. Does not modify
   *  storage. */
  startOnboarding: () => void;
  /** Close the welcome / onboarding overlay without changing storage.
   *  Only valid when at least one profile already exists (otherwise the
   *  visitor would see a crash on the empty dashboard). */
  dismissOnboarding: () => void;

  // Profile surface
  activeProfile: string;
  profiles: ProfileMeta[];
  switchProfile: (name: string) => Promise<void>;
  createProfile: (name: string, template?: 'seed' | 'demo' | 'blank') => Promise<void>;
  deleteProfile: (name: string) => Promise<void>;
  duplicateProfile: (from: string, to: string) => Promise<void>;
  resetProfileToDemo: (name: string) => Promise<void>;

  accounts: Account[]; // active only
  allAccounts: Account[]; // includes archived
  snapshots: Snapshot[];
  assumptions: Assumptions;

  // Legacy purchases (kept for export compat)
  purchases: LargePurchase[];

  // Scenario + event model (v2)
  scenarios: Scenario[];
  activeScenario: Scenario;
  compareScenarios: Scenario[];
  activeScenarioId: string;
  compareScenarioIds: string[];

  currentBalances: Record<string, number>;
  /** Projection driven by the active scenario. */
  projection: ProjectionResult;
  /** Same projection but with all events disabled — used to show impact. */
  baselineProjection: ProjectionResult;
  /** Per-scenario projections for the currently selected compare set. */
  compareProjections: { scenario: Scenario; projection: ProjectionResult }[];
  dashboard: DashboardStats;

  saveAccount: (a: Account) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  saveSnapshot: (s: Snapshot) => Promise<void>;
  deleteSnapshot: (id: string) => Promise<void>;
  saveAssumptions: (a: Assumptions) => Promise<void>;
  savePurchase: (p: LargePurchase) => Promise<void>;
  deletePurchase: (id: string) => Promise<void>;

  saveScenario: (s: Scenario) => Promise<void>;
  deleteScenario: (id: string) => Promise<void>;
  setActiveScenario: (id: string) => Promise<void>;
  setCompareScenarios: (ids: string[]) => Promise<void>;
  toggleCompareScenario: (id: string) => Promise<void>;

  /** Upsert an event into the active scenario. */
  saveEventInActive: (e: EventItem) => Promise<void>;
  /** Delete an event from the active scenario. */
  deleteEventInActive: (id: string) => Promise<void>;

  exportJson: () => Promise<string>;
  importJson: (json: string) => Promise<void>;
  resetAll: () => Promise<void>;
}

const Ctx = createContext<StoreCtx | null>(null);

const POLL_INTERVAL_MS = 1500;

export function StoreProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [activeProfile, setActiveProfileState] = useState<string>(() => getActiveProfile());
  const [profiles, setProfiles] = useState<ProfileMeta[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [assumptions, setAssumptions] = useState<Assumptions | null>(null);
  const [purchases, setPurchases] = useState<LargePurchase[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [meta, setMeta] = useState<ScenarioMeta | null>(null);

  // Track current values without re-creating callbacks. Polling and reload()
  // need to compare-against-current to skip identity churn.
  const accountsRef = useRef(accounts);
  const allAccountsRef = useRef(allAccounts);
  const snapshotsRef = useRef(snapshots);
  const assumptionsRef = useRef(assumptions);
  const purchasesRef = useRef(purchases);
  const scenariosRef = useRef(scenarios);
  const metaRef = useRef(meta);
  useEffect(() => { accountsRef.current = accounts; }, [accounts]);
  useEffect(() => { allAccountsRef.current = allAccounts; }, [allAccounts]);
  useEffect(() => { snapshotsRef.current = snapshots; }, [snapshots]);
  useEffect(() => { assumptionsRef.current = assumptions; }, [assumptions]);
  useEffect(() => { purchasesRef.current = purchases; }, [purchases]);
  useEffect(() => { scenariosRef.current = scenarios; }, [scenarios]);
  useEffect(() => { metaRef.current = meta; }, [meta]);

  const reload = useCallback(async () => {
    const [acc, allAcc, snaps, asm, purch, scens, mt] = await Promise.all([
      listAccounts(),
      listAllAccounts(),
      listSnapshots(),
      getAssumptions(),
      listPurchases(),
      listScenarios(),
      getMeta(),
    ]);
    setIfChanged(accountsRef.current, acc, setAccounts);
    setIfChanged(allAccountsRef.current, allAcc, setAllAccounts);
    setIfChanged(snapshotsRef.current, snaps, setSnapshots);
    setIfChanged(assumptionsRef.current, asm, setAssumptions);
    setIfChanged(purchasesRef.current, purch, setPurchases);
    setIfChanged(scenariosRef.current, scens, setScenarios);
    setIfChanged(metaRef.current, mt, setMeta);
  }, []);

  const reloadProfiles = useCallback(async () => {
    try {
      const list = await listProfiles();
      setProfiles(prev => (sameProfiles(prev, list) ? prev : list));
    } catch {
      /* non-fatal */
    }
  }, []);

  // First-time boot
  //
  // On a fresh browser (zero profiles in storage), we deliberately do NOT
  // auto-materialize a seed. Auto-seeding a Baseline profile drops the
  // first-time visitor into a fully-populated dashboard for a person who
  // is not them — alienating, and defeats the point of a personal planner.
  // Instead we surface the welcome screen; the visitor either onboards or
  // opts into the sample dataset from there.
  //
  // A returning visitor (has at least one profile) is unchanged: seed +
  // reload as always.
  useEffect(() => {
    (async () => {
      const seeded = await hasAnyProfile();
      if (!seeded) {
        setNeedsOnboarding(true);
        // Populate the profiles list slice so downstream state is coherent
        // (an empty array is the honest answer here).
        await reloadProfiles();
        setReady(true);
        return;
      }
      await ensureSeeded();
      await reload();
      await reloadProfiles();
      setReady(true);
    })();
  }, [reload, reloadProfiles]);

  // Poll for external mutations (CLI edits in dev) — only active when the
  // storage backend supports it (file-backed). pollActiveProfile() is a no-op
  // and returns null in IndexedDB mode.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const fresh = await pollActiveProfile();
        if (fresh && !cancelled) {
          // Slice the polled DbExport directly into state — no need to refetch.
          await reload();
          await reloadProfiles();
        }
      } catch {
        /* transient */
      }
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [ready, reload, reloadProfiles]);

  const switchProfile = useCallback(async (name: string) => {
    if (name === activeProfile) return;
    dbSetActiveProfile(name);
    setActiveProfileState(name);
    // Materialize seed if missing, then re-fetch all slices. No page reload.
    await ensureSeeded();
    await reload();
    await reloadProfiles();
  }, [activeProfile, reload, reloadProfiles]);

  const createProfile = useCallback(async (name: string, template: 'seed' | 'demo' | 'blank' = 'seed') => {
    await dbCreateProfile(name, template);
    await reloadProfiles();
  }, [reloadProfiles]);

  const deleteProfile = useCallback(async (name: string) => {
    await dbDeleteProfile(name);
    if (name === activeProfile) {
      // Active profile vanished — fall back to whichever profile is still around.
      const remaining = await listProfiles();
      const fallback = remaining.find(p => p.name !== name)?.name ?? 'main';
      dbSetActiveProfile(fallback);
      setActiveProfileState(fallback);
      await ensureSeeded();
      await reload();
    }
    await reloadProfiles();
  }, [activeProfile, reload, reloadProfiles]);

  const duplicateProfile = useCallback(async (from: string, to: string) => {
    await dbDuplicateProfile(from, to);
    await reloadProfiles();
  }, [reloadProfiles]);

  const resetProfileToDemo = useCallback(async (name: string) => {
    await resetProfileToDemoSeed(name);
    if (name === activeProfile) await reload();
    await reloadProfiles();
  }, [activeProfile, reload, reloadProfiles]);

  const completeOnboarding = useCallback(async (input: OnboardingInput) => {
    await dbCompleteOnboarding(input);
    setActiveProfileState(DEFAULT_PROFILE);
    await reload();
    await reloadProfiles();
    setNeedsOnboarding(false);
  }, [reload, reloadProfiles]);

  const startDemo = useCallback(async () => {
    // Ensure the demo profile exists, then activate it. `switchProfile`
    // would materialize demo via ensureSeeded on the first read, but we
    // want the profile row to exist BEFORE we flip needsOnboarding so
    // downstream reloads pick it up cleanly.
    const list = await listProfiles();
    if (!list.some(p => p.name === DEMO_PROFILE)) {
      await dbCreateProfile(DEMO_PROFILE, 'demo');
    }
    dbSetActiveProfile(DEMO_PROFILE);
    setActiveProfileState(DEMO_PROFILE);
    await ensureSeeded();
    await reload();
    await reloadProfiles();
    setNeedsOnboarding(false);
  }, [reload, reloadProfiles]);

  const startOnboarding = useCallback(() => {
    setNeedsOnboarding(true);
  }, []);

  const dismissOnboarding = useCallback(() => {
    setNeedsOnboarding(false);
  }, []);

  const currentBalances = useMemo(() => latestBalances(snapshots), [snapshots]);

  const activeScenario = useMemo<Scenario | null>(() => {
    if (scenarios.length === 0 || !meta) return null;
    return scenarios.find(s => s.id === meta.activeScenarioId) ?? scenarios[0];
  }, [scenarios, meta]);

  const compareScenarios = useMemo<Scenario[]>(() => {
    if (!meta) return [];
    return meta.compareScenarioIds
      .map(id => scenarios.find(s => s.id === id))
      .filter((s): s is Scenario => !!s && !s.archived);
  }, [scenarios, meta]);

  const projection = useMemo<ProjectionResult>(() => {
    if (!assumptions || !activeScenario) {
      return { years: [], mortgage: [], requiredSpend: 0, requiredNestEgg: 0 };
    }
    return project({
      assumptions,
      accounts,
      currentBalances,
      scenario: activeScenario,
    });
  }, [assumptions, accounts, currentBalances, activeScenario]);

  const baselineProjection = useMemo<ProjectionResult>(() => {
    if (!assumptions) {
      return { years: [], mortgage: [], requiredSpend: 0, requiredNestEgg: 0 };
    }
    return project({
      assumptions,
      accounts,
      currentBalances,
      events: [],
    });
  }, [assumptions, accounts, currentBalances]);

  const compareProjections = useMemo(() => {
    if (!assumptions || compareScenarios.length === 0) return [];
    return compareScenarios.map(s => ({
      scenario: s,
      projection: project({
        assumptions,
        accounts,
        currentBalances,
        scenario: s,
      }),
    }));
  }, [assumptions, accounts, currentBalances, compareScenarios]);

  const dashboard = useMemo<DashboardStats>(() => {
    if (!assumptions || projection.years.length === 0) {
      return {
        currentAge: 0,
        targetRetirementAge: 0,
        currentNetWorth: 0,
        currentInvestable: 0,
        annualSavingsRate: 0,
        currentAnnualSpend: 0,
        projectedNestEgg: 0,
        safeAnnualWithdrawal: 0,
        requiredSpend: 0,
        annualSurplus: 0,
        requiredNestEgg: 0,
        cushion: 0,
        onTrack: false,
        costOf1TodayAtRetirement: 1,
        costOf1TodayIncome: 0,
        mortgagePayoffYear: null,
      };
    }
    return computeDashboard({
      assumptions,
      accounts,
      currentBalances,
      projection,
    });
  }, [assumptions, accounts, currentBalances, projection]);

  const value = useMemo<StoreCtx>(() => {
    const active = activeScenario ?? scenarios[0] ?? fallbackScenario();
    const currentMeta = meta ?? {
      id: 'singleton' as const,
      activeScenarioId: active.id,
      compareScenarioIds: [],
      updatedAt: new Date().toISOString(),
    };
    return {
      ready,
      needsOnboarding,
      completeOnboarding,
      startDemo,
      startOnboarding,
      dismissOnboarding,
      activeProfile,
      profiles,
      switchProfile,
      createProfile,
      deleteProfile,
      duplicateProfile,
      resetProfileToDemo,
      accounts,
      allAccounts,
      snapshots,
      assumptions: assumptions!,
      purchases,
      scenarios,
      activeScenario: active,
      compareScenarios,
      activeScenarioId: currentMeta.activeScenarioId,
      compareScenarioIds: currentMeta.compareScenarioIds,
      currentBalances,
      projection,
      baselineProjection,
      compareProjections,
      dashboard,
      saveAccount: async a => { await putAccount(a); await reload(); },
      deleteAccount: async id => { await dbDeleteAccount(id); await reload(); },
      saveSnapshot: async s => { await putSnapshot(s); await reload(); },
      deleteSnapshot: async id => { await dbDeleteSnapshot(id); await reload(); },
      saveAssumptions: async a => { await putAssumptions(a); await reload(); },
      savePurchase: async p => { await putPurchase(p); await reload(); },
      deletePurchase: async id => { await dbDeletePurchase(id); await reload(); },
      saveScenario: async s => { await putScenario(s); await reload(); },
      deleteScenario: async id => {
        await dbDeleteScenario(id);
        if (currentMeta.activeScenarioId === id) {
          const fallback =
            scenarios.find(s => s.id === 'baseline' && s.id !== id) ??
            scenarios.find(s => s.id !== id);
          if (fallback) {
            await putMeta({
              ...currentMeta,
              activeScenarioId: fallback.id,
              compareScenarioIds: currentMeta.compareScenarioIds.filter(x => x !== id),
            });
          }
        } else if (currentMeta.compareScenarioIds.includes(id)) {
          await putMeta({
            ...currentMeta,
            compareScenarioIds: currentMeta.compareScenarioIds.filter(x => x !== id),
          });
        }
        await reload();
      },
      setActiveScenario: async id => {
        await putMeta({ ...currentMeta, activeScenarioId: id });
        await reload();
      },
      setCompareScenarios: async ids => {
        await putMeta({ ...currentMeta, compareScenarioIds: ids });
        await reload();
      },
      toggleCompareScenario: async id => {
        const next = currentMeta.compareScenarioIds.includes(id)
          ? currentMeta.compareScenarioIds.filter(x => x !== id)
          : [...currentMeta.compareScenarioIds, id];
        await putMeta({ ...currentMeta, compareScenarioIds: next });
        await reload();
      },
      saveEventInActive: async e => {
        const target = active;
        const existingIdx = target.events.findIndex(x => x.id === e.id);
        const nextEvents =
          existingIdx >= 0
            ? target.events.map((x, i) => (i === existingIdx ? e : x))
            : [...target.events, e];
        await putScenario({ ...target, events: nextEvents });
        await reload();
      },
      deleteEventInActive: async id => {
        const target = active;
        await putScenario({
          ...target,
          events: target.events.filter(e => e.id !== id),
        });
        await reload();
      },
      exportJson: async () => JSON.stringify(await exportAll(), null, 2),
      importJson: async (json: string) => {
        const data = JSON.parse(json);
        await importAll(data);
        await reload();
      },
      resetAll: async () => {
        await wipe();
        await ensureSeeded();
        await reload();
      },
    };
  }, [
    ready,
    needsOnboarding,
    completeOnboarding,
    startDemo,
    startOnboarding,
    dismissOnboarding,
    activeProfile,
    profiles,
    switchProfile,
    createProfile,
    deleteProfile,
    duplicateProfile,
    resetProfileToDemo,
    accounts,
    allAccounts,
    snapshots,
    assumptions,
    purchases,
    scenarios,
    meta,
    activeScenario,
    compareScenarios,
    currentBalances,
    projection,
    baselineProjection,
    compareProjections,
    dashboard,
    reload,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function fallbackScenario(): Scenario {
  return {
    id: 'baseline',
    name: 'Baseline',
    icon: '🗻',
    color: '#3b54e8',
    order: 0,
    archived: false,
    events: [],
    createdAt: new Date().toISOString(),
  };
}

function setIfChanged<T>(current: T, next: T, set: (v: T) => void): void {
  // JSON-stringify equality is fine for our slice sizes (max O(hundreds) of
  // events / snapshots / accounts). Skipping no-op updates keeps useMemo
  // dependencies stable so projection / dashboard don't recompute on every
  // poll tick when nothing actually changed.
  if (JSON.stringify(current) === JSON.stringify(next)) return;
  set(next);
}

function sameProfiles(a: ProfileMeta[], b: ProfileMeta[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].name !== b[i].name || a[i].mtime !== b[i].mtime) return false;
  }
  return true;
}

export function useStore(): StoreCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStore must be used within StoreProvider');
  return v;
}
